import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  IInputRangeWithSliderProps,
  RangePropertyNames,
  RangeValue,
} from '@components-shared/forms/InputRangeWithSlider/interfaces';
import { keyMap } from '@locales/lokalise/keyMap';
import { Slider, InputNumber } from 'antd';
import styles from './styles.module.scss';

const DEFAULT_STEP = 1;

export const InputRangeWithSlider = <T,>({
  keyName,
  onChange,
  limitToValue,
  limitFromValue,
  initialState,
}: IInputRangeWithSliderProps<T>) => {
  const [value, setValue] = useState<RangeValue>({ from: 0, to: 0 });
  const { i18n } = useTranslation();

  const setInitialValue = useCallback((): void => {
    const values = Object.values(initialState).every((num) => num === 0);

    if (limitFromValue !== undefined && limitToValue !== undefined && values) {
      setRangeValues(limitFromValue, limitToValue);

      return;
    }

    if (initialState) {
      const initialFromValue = initialState.from;
      const initialToValue = initialState.to;

      setRangeValues(initialFromValue, initialToValue);

      return;
    }
  }, [initialState, limitFromValue, limitToValue]);

  useEffect(
    () => {
      setInitialValue();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const setRangeValues = (from: number, to: number): void => {
    setValue({ from, to });
  };

  const onChangeInput = (name: RangePropertyNames, newValue: number | null): void => {
    if (typeof newValue === 'number') {
      setValue((prevState) => ({
        ...prevState,
        [name]: newValue,
      }));

      const updatedValue = { ...value, [name]: newValue };

      onChange(keyName, updatedValue as T);
    }
  };

  const onChangeSlider = ([from, to]: number[]): void => {
    setValue({ from, to });
    onChange(keyName, { from, to } as T);
  };

  const checkIfCorrectLimitsProvided = (): boolean => {
    return limitFromValue !== undefined && limitToValue !== undefined && limitFromValue <= limitToValue;
  };

  const getSliderStep = (): number => {
    return checkIfCorrectLimitsProvided() ? ((limitToValue ?? 0) - (limitFromValue ?? 0)) / 100 : DEFAULT_STEP;
  };

  const fromOnChangeHandler = (value: number | null): void => {
    onChangeInput('from', value);
  };

  const toOnChangeHandler = (value: number | null): void => {
    onChangeInput('to', value);
  };

  const sliderStep = getSliderStep();

  const isLimitsProvided = checkIfCorrectLimitsProvided();

  return (
    <>
      <div className={styles.inputsContainer}>
        <div>
          <span className={styles.inputTitle}>{i18n.t(keyMap.formsFrom)}</span>
          <InputNumber
            className="form__input"
            type="number"
            name="from"
            step={sliderStep}
            max={limitToValue}
            min={limitFromValue}
            onChange={fromOnChangeHandler}
            value={value.from}
            placeholder={i18n.t(keyMap.formsFrom)}
          />
          {isLimitsProvided && (
            <span className={styles.inputHint}>{i18n.t(keyMap.formsMin, { minValue: limitFromValue })}</span>
          )}
        </div>

        <div className={styles.hyphen}>
          <span>&mdash;</span>
        </div>

        <div>
          <span className={styles.inputTitle}>{i18n.t(keyMap.formsTo)}</span>
          <InputNumber
            className="form__input"
            type="number"
            name="to"
            max={limitToValue}
            min={limitFromValue}
            onChange={toOnChangeHandler}
            step={sliderStep}
            value={value.to}
            placeholder={i18n.t(keyMap.formsTo)}
          />
          {isLimitsProvided && (
            <span className={styles.inputHint}>{i18n.t(keyMap.formsMax, { maxValue: limitToValue })}</span>
          )}
        </div>
      </div>

      {isLimitsProvided && (
        <Slider
          range
          step={sliderStep}
          min={limitFromValue}
          max={limitToValue}
          value={[value.from, value.to]}
          onChange={onChangeSlider}
          className={styles.slider}
        />
      )}
    </>
  );
};
